import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import './InstanaROI.scss';
import './NoteDrawer.scss';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import {
  Disruptions,
  ITOpsEmployees,
  Developers,
  DisruptionBucketHours,
  AverageTime,
  SKELETON_IMAGE,
  UT30InstanaROI,
} from '../constants/defaults';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import {
  determineCountry,
  LangDefaults,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import BVAQuestions from './BVAQuestions';
import BVAResults from './BVAResults';
import Cookies from 'js-cookie';
import * as URX_FORM from '../constants/urx-form';
import { setEffiencyGains, setRevenueSavings } from '../hooks/useRoi';
import { Button } from 'carbon-components-react';
import { Close32 } from '@carbon/icons-react';
import NoteDrawer from './NoteDrawer';
import Thanks from './Thanks';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import useTimer from 'src/common/hooks/useTimer';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';

export interface InstanaROIProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const InstanaROI = ({ langCode, test, countryToTest }: InstanaROIProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);

  /* Instana States */
  const [disruptions, setDisruptions] = useState(Disruptions.default);
  const [itOpsEmployees, setITOpsEmployees] = useState(ITOpsEmployees.default);
  const [developers, setDevelopers] = useState(Developers.default);
  const [avgTime, setAvgTime] = useState(AverageTime.default);
  const [defaultCheckedAvgTime, setDefaultCheckedAvgTime] = useState(true);
  const [prometheus, setPrometheus] = useState(false);
  const [openTracing, setOpenTracing] = useState(false);
  const [openTelemetry, setOpenTelemetry] = useState(false);
  const [jaeger, setJaeger] = useState(false);
  const [zipkin, setZipkin] = useState(false);
  const [openCensus, setOpenCensus] = useState(false);
  const [viewBVAResults, setViewBVAResults] = useState(false);
  const [preventions, setPrevention] = useState(0);
  const [speedGain, setSpeedGain] = useState(0);
  const [avgTimePerDistruptions, setAvgTimePerDistruptions] = useState(
    DisruptionBucketHours,
  );
  const [openSourceTechnoList, setOpenSourceTechnoList] = useState('');

  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [environment] = useEnv();
  const { bpName, logo, companyId, desc, mssp } = useBPName(
    UT30InstanaROI,
    true,
  );
  const cookieURL = useCookiesURL();
  const [currency, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  // Set us-en
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  const [moveSlider, setMoveSlider] = useState(false);

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Reset all the questions to default values
  const reset = () => {
    setDisruptions(Disruptions.default);
    setITOpsEmployees(ITOpsEmployees.default);
    setDevelopers(Developers.default);
    setAvgTime(AverageTime.default);
    setAvgTimePerDistruptions(DisruptionBucketHours);
    setDefaultCheckedAvgTime(true);
    setPrometheus(false);
    setOpenTracing(false);
    setOpenTelemetry(false);
    setJaeger(false);
    setZipkin(false);
    setOpenCensus(false);
    changeIsPdf(false);
    setMoveSlider(false);
  };
  // Show URX form in Price Estimator (we no longer open the ROI URX)
  localStorage.setItem('showInstanaForm', 'false');
  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    false,
  );

  // Determine Effiency Gains
  const [
    developerEffiencyGains,
    itOpsEffiencyGains,
    openSourceGains,
    totalEffiencyGains,
  ] = setEffiencyGains({
    developers,
    preventions,
    speedGain,
    avgTimePerDistruptions,
    itOpsEmployees,
    openSourceTechnoList,
    currency,
    userLocale,
  });

  // Determine Revenue Savings
  const [
    tenThousandDollarPerHour,
    thirtThousandDollarPerHour,
    fiftyThousandDollarPerHour,
    twoHunderedThousandDollarPerHour,
  ] = setRevenueSavings({
    preventions,
    speedGain,
    avgTimePerDistruptions,
    totalEffiencyGains,
  });

  const [resultsImage, setResultsImage] = useState(SKELETON_IMAGE);
  const [summaryImage, setSummaryImage] = useState(SKELETON_IMAGE);
  const [isPdf, changeIsPdf] = useState(false);
  const [pdfExport, setPdfExport] = useState(false);
  // Update Cookies
  useEffect(() => {
    setCookie();
  }, [
    disruptions,
    avgTime,
    preventions,
    developers,
    itOpsEmployees,
    openSourceTechnoList,
    preventions,
    speedGain,
    developerEffiencyGains,
    itOpsEffiencyGains,
    openSourceGains,
    totalEffiencyGains,
    tenThousandDollarPerHour,
    fiftyThousandDollarPerHour,
    thirtThousandDollarPerHour,
    twoHunderedThousandDollarPerHour,
  ]);

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
  };

  const setCookie = () => {
    const cookieContent =
      cookieURL +
      ' Inputs from the user: ' +
      ' || Total Number of Disruptions per month: ' +
      disruptions +
      ' | Average Time spent solving disruptions: ' +
      avgTime +
      ' | Total Number of Developers: ' +
      developers +
      ' | Total Number of IT OPS Employees: ' +
      itOpsEmployees +
      ' | Open Source Technologies: ' +
      openSourceTechnoList +
      ' || Calculations results: ' +
      ' | Total Preventions: ' +
      preventions +
      ' | Total Speed Gains: ' +
      speedGain +
      ' | Total Developer Efficiency Gains: ' +
      developerEffiencyGains +
      ' | Total ITOPS Efficiency Gains: ' +
      itOpsEffiencyGains +
      ' | Total Efficiency Gains with Open Source Technologies: ' +
      openSourceGains +
      ' | Total Efficiency Gains: ' +
      totalEffiencyGains +
      ' | Revenue Savings - $10K/h: ' +
      tenThousandDollarPerHour +
      ' | Revenue Savings - $50K/hr: ' +
      fiftyThousandDollarPerHour +
      ' | Revenue Savings - $150K/hr: ' +
      fiftyThousandDollarPerHour +
      ' | Revenue Savings - Greater than $400K/hr: ' +
      twoHunderedThousandDollarPerHour +
      ' - ' +
      (getBp() ? `|Preferred BP:${getBp()}` : '');
    const text = JSON.stringify({
      source: URX_FORM.FORM_ID,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA: cookieContent,
        Q_ASSET_URL: window.location.href, // ISC - Asset URL
        Q_ASSET_NAME: 'IBM Instana ROI Estimator', // ISC - Asset Name
      },
    });
    const jsonCookie = {
      message: cookieContent,
      content: {
        disruptions,
        preventions,
        avgTime,
        itOpsEmployees,
        developers,
        developerEffiencyGains,
        itOpsEffiencyGains,
        openSourceGains,
        totalEffiencyGains,
        speedGain,
        openSourceTechnoList,
      },
    };
    localStorage.setItem('InstanaFormContent', JSON.stringify(jsonCookie));
    Cookies.set('urxdcq', text);
  };

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  /* Timer handler to show contact form autimativally after 2 mins*/
  const { timeout, init } = useTimer(false, 120000);

  useEffect(() => {
    if (moveSlider) {
      init(true);
    }
  }, [moveSlider]);

  useEffect(() => {
    if (timeout) {
      setCookie();
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang,
        companyId,
      );
      setDrawerOpen(true);
      setMoveSlider(false);
    }
  }, [timeout]);

  return (
    <div className="instana-roi">
      <Helmet>
        <script src={urxScriptLoader}></script>
        <script src={urxScript}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          disruptions={disruptions}
          preventions={preventions}
          avgTime={avgTime}
          itOpsEmployees={itOpsEmployees}
          developers={developers}
          developerEffiencyGains={developerEffiencyGains}
          itOpsEffiencyGains={itOpsEffiencyGains}
          openSourceGains={openSourceGains}
          totalEffiencyGains={totalEffiencyGains}
          speedGain={speedGain}
          openSourceTechnoList={openSourceTechnoList}
          resultsImage={resultsImage}
          summaryImage={summaryImage}
          isPdf={isPdf}
          pdfExport={pdfExport}
        />
      ) : (
        <div className="bx--grid" id="InstanaGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          {!viewBVAResults ? (
            <BVAQuestions
              localExist={localExist}
              toggleCountry={toggleCountry}
              modalLocalCountry={modalLocalCountry}
              modalLocalLanguage={modalLocalLanguage}
              modalLocalCurrency={modalLocalCurrency}
              selectedLocale={selectedLocale}
              disruptions={disruptions}
              itOpsEmployees={itOpsEmployees}
              developers={developers}
              defaultCheckedAvgTime={defaultCheckedAvgTime}
              setDefaultCheckedAvgTime={setDefaultCheckedAvgTime}
              setAvgTime={setAvgTime}
              setAvgTimePerDistruptions={setAvgTimePerDistruptions}
              setDisruptions={setDisruptions}
              setITOpsEmployees={setITOpsEmployees}
              setDevelopers={setDevelopers}
              setViewBVAResults={setViewBVAResults}
              reset={reset}
              prometheus={prometheus}
              jaeger={jaeger}
              zipkin={zipkin}
              openTelemetry={openTelemetry}
              openTracing={openTracing}
              openCensus={openCensus}
              setPrometheus={setPrometheus}
              setJaeger={setJaeger}
              setOpenCensus={setOpenCensus}
              setOpenTelemetry={setOpenTelemetry}
              setOpenTracing={setOpenTracing}
              setZipkin={setZipkin}
              setMoveSlider={setMoveSlider}
            />
          ) : (
            <BVAResults
              lang={lang}
              disruptions={disruptions}
              preventions={preventions}
              speedGain={speedGain}
              itOpsEmployees={itOpsEmployees}
              developers={developers}
              avgTime={avgTime}
              setSpeedGain={setSpeedGain}
              setPrevention={setPrevention}
              setViewBVAResults={setViewBVAResults}
              reset={reset}
              prometheus={prometheus}
              jaeger={jaeger}
              zipkin={zipkin}
              openTelemetry={openTelemetry}
              openTracing={openTracing}
              openCensus={openCensus}
              openSourceTechnoList={openSourceTechnoList}
              setOpenSourceTechnoList={setOpenSourceTechnoList}
              developerEffiencyGains={developerEffiencyGains}
              itOpsEffiencyGains={itOpsEffiencyGains}
              openSourceGains={openSourceGains}
              totalEffiencyGains={totalEffiencyGains}
              tenThousandDollarPerHour={tenThousandDollarPerHour}
              thirtyThousandDollarPerHour={thirtThousandDollarPerHour}
              fiftyThousandDollarPerHour={fiftyThousandDollarPerHour}
              twoHunderedThousandDollarPerHour={
                twoHunderedThousandDollarPerHour
              }
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              bpName={bpName}
              changeIsPdf={changeIsPdf}
              setResultsImage={setResultsImage}
              setSummaryImage={setSummaryImage}
              isPdf={isPdf}
              urxlang={urxlang}
              companyId={companyId}
              environment={environment}
              setPdfExport={setPdfExport}
            />
          )}
          <NoteDrawer
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            changeIsPdf={changeIsPdf}
          />
          <div
            id="PutDrawerToFront"
            className={`instana-roi-drawer
        ${drawerOpen ? 'opened' : 'closed'} instana-roi-drawer-transition`}
          >
            <div className="instana-roi-drawer-inner-container">
              <div className="bx--row mar6">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <div className="instana-roi-drawer-close-container">
                    <Button
                      className="instana-roi-drawer-close-btn"
                      kind="ghost"
                      renderIcon={Close32}
                      size="sm"
                      onClick={() => {
                        setDrawerOpen(false);
                        changeIsPdf(false);
                      }}
                    >
                      Closed
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  {bpName ? (
                    <BusinessPartner
                      bpName={bpName}
                      logo={logo}
                      urxForm={true}
                    />
                  ) : null}
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <div className="instana-roi-drawer-notes-container">
                    <div id="urx-form-container">
                      <div no-toc id="urx-form"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstanaROI;
